import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Lock, CheckCircle2, Clock, Award, ArrowRight, Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

// Lightweight markdown → HTML helper (handles ##, ###, lists, bold).
// IMPORTANT: every raw character from the markdown source is HTML-escaped
// FIRST, so any `<script>`/`onerror=` etc. injected via the content (e.g. a
// compromised LLM response) is rendered as literal text — not executed.
// Only the safe inline tokens we generate ourselves (<strong>, <p>, <h2>,
// <h3>, <ul>, <li>) make it into the output.
const _HTML_ESC = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => _HTML_ESC[c]);
}
function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    // 1) Escape EVERYTHING so source markdown can never inject raw HTML.
    // 2) Then re-introduce only the bold inline token we control.
    const safe = escapeHtml(raw).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^###\s+/.test(safe)) { flushList(); out.push(`<h3 class="font-display text-xl font-black text-emerald-900 mt-6 mb-2">${safe.replace(/^###\s+/, "")}</h3>`); continue; }
    if (/^##\s+/.test(safe))  { flushList(); out.push(`<h2 class="font-display text-2xl font-black text-emerald-900 mt-7 mb-3">${safe.replace(/^##\s+/, "")}</h2>`); continue; }
    if (/^[-*]\s+/.test(safe)) { if (!inList) { out.push('<ul class="list-disc list-outside ml-6 space-y-1 my-3 text-stone-700">'); inList = true; } out.push(`<li>${safe.replace(/^[-*]\s+/, "")}</li>`); continue; }
    if (safe.trim() === "")    { flushList(); continue; }
    flushList(); out.push(`<p class="my-2.5 text-stone-700 leading-relaxed">${safe}</p>`);
  }
  flushList();
  return out.join("\n");
}

function formatDuration(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60); const ss = s % 60;
  if (m === 0) return `${ss}s`;
  if (m < 60) return `${m}m ${ss}s`;
  const h = Math.floor(m / 60); const mm = m % 60;
  return `${h}h ${mm}m`;
}

export default function Training() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [openChapterId, setOpenChapterId] = useState(null);
  const [completing, setCompleting] = useState(false);
  const tickerRef = useRef(null);
  const activeChapterRef = useRef(null);

  const load = async () => {
    try { const r = await api.get("/training/course"); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load course"); }
  };
  useEffect(() => { load(); }, []);

  // Tick every 5 seconds for the currently open chapter (only while it's not complete and the tab is visible).
  useEffect(() => {
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (!openChapterId) return;
    const ch = (data?.chapters || []).find((c) => c.id === openChapterId);
    if (!ch || ch.completed) return;
    activeChapterRef.current = openChapterId;
    tickerRef.current = setInterval(async () => {
      if (document.hidden) return;
      try {
        const r = await api.post(`/training/chapters/${activeChapterRef.current}/tick`, { seconds: 5 });
        // Update local state for the active chapter
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            chapters: prev.chapters.map((c) =>
              c.id === activeChapterRef.current ? { ...c, seconds_spent: r.data.seconds_spent } : c
            ),
          };
        });
      } catch { /* network blip — try again next tick */ void 0; }
    }, 5000);
    return () => { if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; } };
  }, [openChapterId, data?.chapters]);

  const openChapter = (ch) => {
    if (!ch.unlocked) { toast.error("Finish previous chapters first to unlock this one."); return; }
    setOpenChapterId(ch.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const completeChapter = async (ch) => {
    if (ch.seconds_spent < ch.min_seconds) {
      toast.error(`Stay on this chapter for at least ${formatDuration(ch.min_seconds)} (you've done ${formatDuration(ch.seconds_spent)}).`);
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/training/chapters/${ch.id}/complete`);
      toast.success("Chapter complete! Next one unlocked.");
      setOpenChapterId(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark complete"); }
    finally { setCompleting(false); }
  };

  if (!user) return null;
  if (!data) return <div className="text-stone-500" data-testid="training-loading">Loading training...</div>;

  if (data.chapters.length === 0) {
    return (
      <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center" data-testid="training-empty">
        <BookOpen className="w-10 h-10 mx-auto text-amber-500" />
        <div className="font-display text-xl font-black mt-3">Training content coming soon</div>
        <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">
          Your admin hasn&apos;t published the foundation course yet. Check back soon.
        </p>
      </div>
    );
  }

  const openCh = data.chapters.find((c) => c.id === openChapterId);

  // ----- CHAPTER READER VIEW -----
  if (openCh) {
    const pct = Math.min(100, Math.round((openCh.seconds_spent / Math.max(1, openCh.min_seconds)) * 100));
    const remaining = Math.max(0, openCh.min_seconds - openCh.seconds_spent);
    return (
      <div className="space-y-6" data-testid={`training-chapter-${openCh.id}`}>
        <button onClick={() => setOpenChapterId(null)} className="inline-flex items-center text-xs font-bold text-stone-600 hover:text-emerald-900" data-testid="training-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to course
        </button>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Chapter {openCh.order} of {data.chapters.length}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-1" data-testid="training-chapter-title">{openCh.title}</h1>
          {openCh.summary && <p className="text-stone-500 mt-2 max-w-3xl">{openCh.summary}</p>}
        </div>

        <div className="sticky top-2 z-10 bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid="training-chapter-progress">
          <div className="flex items-center gap-2 text-emerald-900">
            <Clock className="w-4 h-4" />
            <span className="font-mono font-bold text-sm" data-testid="training-chapter-progress-text">
              {formatDuration(openCh.seconds_spent)} / {formatDuration(openCh.min_seconds)}
            </span>
          </div>
          <div className="flex-1 min-w-[150px]">
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-700 transition-all" style={{ width: `${pct}%` }} data-testid="training-chapter-progress-bar" />
            </div>
          </div>
          {openCh.completed ? (
            <span className="text-xs font-bold text-emerald-700 inline-flex items-center" data-testid="training-chapter-already-done"><CheckCircle2 className="w-4 h-4 mr-1" /> Completed</span>
          ) : remaining === 0 ? (
            <Button onClick={() => completeChapter(openCh)} disabled={completing} data-testid="training-chapter-complete-btn"
              className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Complete
            </Button>
          ) : (
            <span className="text-xs text-stone-500" data-testid="training-chapter-remaining">{formatDuration(remaining)} remaining</span>
          )}
        </div>

        <article
          data-testid="training-chapter-body"
          className="bg-white rounded-2xl border border-stone-200 p-7 sm:p-10 prose prose-stone max-w-none"
          dangerouslySetInnerHTML={{
            // Double-defence: mdToHtml already escapes raw input, but we run
            // the final HTML through DOMPurify so a future relaxation of the
            // markdown renderer can't smuggle through `<script>` / `onerror=`.
            __html: DOMPurify.sanitize(mdToHtml(openCh.content_markdown), {
              ALLOWED_TAGS: ["p", "h2", "h3", "strong", "em", "ul", "ol", "li", "br", "code"],
              ALLOWED_ATTR: [],
            }),
          }}
        />
      </div>
    );
  }

  // ----- COURSE OVERVIEW VIEW -----
  const completedCount = data.chapters.filter((c) => c.completed).length;
  const totalSeconds = data.chapters.reduce((a, c) => a + (c.min_seconds || 0), 0);
  const totalDoneSeconds = data.chapters.reduce((a, c) => a + Math.min(c.seconds_spent, c.min_seconds), 0);
  const overallPct = Math.round((totalDoneSeconds / Math.max(1, totalSeconds)) * 100);

  return (
    <div className="space-y-8" data-testid="training-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Training</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2" data-testid="training-course-title">{data.course.title}</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">{data.course.description}</p>
        </div>
        {data.passed && (
          <Link to="/app/certificates" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 text-white px-5 py-2.5 text-sm font-bold hover:bg-emerald-950" data-testid="training-view-certificate">
            <Award className="w-4 h-4" /> View Certificate <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* Overall progress card */}
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-amber-50 rounded-2xl p-6 flex items-center gap-6 flex-wrap" data-testid="training-overall">
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Your Progress</div>
          <div className="font-display text-2xl font-black mt-1" data-testid="training-overall-text">
            {completedCount} / {data.chapters.length} chapters · {overallPct}%
          </div>
          <div className="h-2 bg-emerald-700/50 rounded-full overflow-hidden mt-3 max-w-md">
            <div className="h-full bg-amber-400" style={{ width: `${overallPct}%` }} />
          </div>
          <div className="text-xs text-amber-200/80 mt-2">Pass mark: {data.course.pass_mark}% · Exam: {data.course.exam_question_count} random questions</div>
        </div>
      </div>

      {/* Chapter list */}
      <div className="space-y-3" data-testid="training-chapter-list">
        {data.chapters.map((ch) => {
          const pct = Math.min(100, Math.round((ch.seconds_spent / Math.max(1, ch.min_seconds)) * 100));
          return (
            <button
              key={ch.id}
              onClick={() => openChapter(ch)}
              disabled={!ch.unlocked}
              data-testid={`training-chapter-card-${ch.id}`}
              className={`w-full text-left bg-white border rounded-2xl p-5 transition-all flex items-center gap-4 ${
                ch.unlocked ? "border-stone-200 hover:border-emerald-900 hover:shadow-md cursor-pointer" : "border-stone-200 opacity-60 cursor-not-allowed"
              }`}
            >
              <div className={`w-12 h-12 rounded-xl shrink-0 flex items-center justify-center font-display font-black text-lg ${
                ch.completed ? "bg-emerald-900 text-white" : ch.unlocked ? "bg-amber-100 text-amber-900" : "bg-stone-100 text-stone-400"
              }`} data-testid={`training-chapter-card-status-${ch.id}`}>
                {ch.completed ? <CheckCircle2 className="w-6 h-6" /> : ch.unlocked ? ch.order : <Lock className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-black truncate">{ch.title}</div>
                {ch.summary && <div className="text-xs text-stone-500 mt-0.5 truncate">{ch.summary}</div>}
                <div className="mt-2 flex items-center gap-3 text-xs text-stone-500">
                  <span className="font-mono">{formatDuration(Math.min(ch.seconds_spent, ch.min_seconds))} / {formatDuration(ch.min_seconds)}</span>
                  <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden flex-1 max-w-[200px]">
                    <div className={`h-full ${ch.completed ? "bg-emerald-700" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
              <div className="hidden sm:flex items-center text-stone-400">
                {ch.unlocked && !ch.completed && <Play className="w-5 h-5" />}
                {ch.completed && <CheckCircle2 className="w-5 h-5 text-emerald-700" />}
                {!ch.unlocked && <Lock className="w-5 h-5" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Exam launcher */}
      <div className={`bg-white border-2 rounded-2xl p-6 flex items-center gap-5 flex-wrap ${data.exam_ready ? "border-emerald-900" : "border-stone-200"}`} data-testid="training-exam-launcher">
        <div className={`w-14 h-14 rounded-xl shrink-0 flex items-center justify-center ${data.exam_ready ? "bg-emerald-900 text-amber-300" : "bg-stone-100 text-stone-400"}`}>
          <Award className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-display text-xl font-black">Final Exam</div>
          <p className="text-sm text-stone-600 mt-0.5">
            {data.passed
              ? "You've already passed. You can re-take it anytime for practice."
              : data.exam_ready
              ? `Answer ${data.course.exam_question_count} random questions. Score ${data.course.pass_mark}% or higher to earn your certificate.`
              : "Complete all chapters above to unlock the exam."}
          </p>
          {data.latest_attempt && (
            <div className="text-xs text-stone-500 mt-1" data-testid="training-latest-attempt">
              Last attempt: {data.latest_attempt.score}/{data.latest_attempt.total} ({data.latest_attempt.percentage}%) — {data.latest_attempt.passed ? "Passed" : "Try again"}
            </div>
          )}
        </div>
        <Button
          onClick={() => navigate("/app/training/exam")}
          disabled={!data.exam_ready}
          data-testid="training-start-exam-btn"
          className={`rounded-full font-bold ${data.exam_ready ? "bg-emerald-900 hover:bg-emerald-950 text-white" : ""}`}
        >
          {data.passed ? "Retake Exam" : "Start Exam"} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
